import type { DatiMisureFotovoltaicoDTO } from '@/models/dto/fotovoltaico/dati-misure-fotovoltaico-dto'
import type { NuovoFotovoltaicoDTO } from '@/models/dto/fotovoltaico/fotovoltaico-dto'
import type { FotovoltaicoPaginationDTO } from '@/models/dto/fotovoltaico/fotovoltaico-pagination-dto'
import type { StimaFotovoltaicoDTO } from '@/models/dto/fotovoltaico/stima-fotovoltaico-dto'
import type { TipologieFotovoltaicoDTO } from '@/models/dto/fotovoltaico/tipologia-fotovoltaico-dto'
import {
    mapToPhotovoltaicEstimate,
    mapToStimaFotovoltaicoDTO,
} from '@/models/mappers/photovoltaic/photovoltaic-estimate-mapper'
import { mapToFotovoltaicoDTO, mapToPhotovoltaicPlant } from '@/models/mappers/photovoltaic/photovoltaic-plant-mapper'
import { mapToPhotovoltaicPlants } from '@/models/mappers/photovoltaic/photovoltaic-plants-mapper'
import { mapToPhotovoltaicReadings } from '@/models/mappers/photovoltaic/photovoltaic-readings-mapper'
import { mapToPhotovoltaicModel } from '@/models/mappers/photovoltaic/photovoltaic-type-mapper'
import type { PhotovoltaicEstimate } from '@/models/types/photovoltaic/photovoltaic-estimate'
import type { PhotovoltaicModel } from '@/models/types/photovoltaic/photovoltaic-model'
import type { NewPhotovoltaicPlant, PhotovoltaicPlant } from '@/models/types/photovoltaic/photovoltaic-plant'
import type { PhotovoltaicPlantsWithPagination } from '@/models/types/photovoltaic/photovoltaic-plants-with-pagination'
import type { PhotovoltaicReadings } from '@/models/types/photovoltaic/photovoltaic-readings'
import type { AggretationStrategy, PhotovoltaicStrategy, ProductionType } from '@/models/types/plc/aggregation-strategy'
import type { OrderType } from '@/types/table'
import { capitalizeFirstLetter } from '@/utils/utils'
import type { FetchOptionsType } from './api'
import { deleteJsonRequest, getRequest, patchJsonRequest, postJsonRequest, putJsonRequest } from './make-request'

type PhotovoltaicGetOptions = {
    order?: OrderType
    sort?: keyof NuovoFotovoltaicoDTO
    search?: string
    page?: string
} & Partial<Record<keyof NewPhotovoltaicPlant, string>>

type PhotovoltaicReadingsGetOptions = {
    from?: string
    to?: string
    fromDate?: string
    strategy?: AggretationStrategy
    productionType?: ProductionType
    productionFTV?: number
    photovoltaicDateStrategy?: PhotovoltaicStrategy
    onlyLastValue?: boolean
}

type PhotovoltaicCrudType = (fetchOptions?: FetchOptionsType) => {
    getOne: (id: number) => Promise<PhotovoltaicPlant>
    getOneByFacilityId: (id: number) => Promise<PhotovoltaicPlant>
    getMany: (
        itemsPerPage: string,
        options?: PhotovoltaicGetOptions,
        onlyEnabled?: boolean,
    ) => Promise<PhotovoltaicPlantsWithPagination>
    patchFacility: (id: number, facilityId: number) => Promise<PhotovoltaicPlant>
    getAll: () => Promise<PhotovoltaicPlant[]>
    post: (photovoltaic: NewPhotovoltaicPlant) => Promise<PhotovoltaicPlant>
    getAllModels: () => Promise<PhotovoltaicModel[]>
    put: (photovoltaic: NewPhotovoltaicPlant) => Promise<PhotovoltaicPlant>
    delete: (id: number) => Promise<void>
    getProduction: (id: number, options?: PhotovoltaicReadingsGetOptions) => Promise<PhotovoltaicReadings>
    getConsumption: (id: number, options?: PhotovoltaicReadingsGetOptions) => Promise<PhotovoltaicReadings>
    getCo2: (id: number, options?: PhotovoltaicReadingsGetOptions) => Promise<PhotovoltaicReadings>
    getEstimate: (facilityId: number) => Promise<PhotovoltaicEstimate>
    putEstimate: (facilityId: number, estimate: PhotovoltaicEstimate) => Promise<PhotovoltaicEstimate>
}

const PHOTOVOLTAIC_URL =
    typeof window === 'undefined'
        ? `${process.env.API_URL}/api/v1/Fotovoltaico`
        : `${location.origin}/api/v1/Fotovoltaico`
const PHOTOVOLTAIC_TAG = 'photovoltaic'

const photovoltaicCRUD: PhotovoltaicCrudType = (fetchOptions) => ({
    getOne: async (id) => {
        const url = new URL(`${PHOTOVOLTAIC_URL}/${id.toString()}`)
        const fotovoltaicoDTO = await getRequest<NuovoFotovoltaicoDTO>({
            url,
            next: { tags: [PHOTOVOLTAIC_TAG] },
            ...fetchOptions,
        })
        const plant = mapToPhotovoltaicPlant(fotovoltaicoDTO) as PhotovoltaicPlant
        return plant
    },
    getOneByFacilityId: async (id) => {
        const url = new URL(`${PHOTOVOLTAIC_URL}/impianto/${id.toString()}`)
        const fotovoltaicoDTO = await getRequest<NuovoFotovoltaicoDTO>({
            url,
            next: { tags: [PHOTOVOLTAIC_TAG] },
            ...fetchOptions,
        })
        const plant = mapToPhotovoltaicPlant(fotovoltaicoDTO) as PhotovoltaicPlant
        return plant
    },
    getMany: async (itemsPerPage, options, onlyEnabled = false) => {
        const url = new URL(`${PHOTOVOLTAIC_URL}/Pagination`)
        url.searchParams.append('PageSize', itemsPerPage)
        options?.page && url.searchParams.append('PageIndex', options.page)
        options?.order && url.searchParams.append('Descending', options.order === 'desc' ? 'true' : 'false')
        options?.sort && url.searchParams.append('SortBy', capitalizeFirstLetter(options.sort))

        onlyEnabled && url.searchParams.append('OnlyActive', 'true')

        options?.search && url.searchParams.append('Filters[search]', options.search)

        options?.name && url.searchParams.append('Filters[nome]', options.name)
        options?.active && url.searchParams.append('Filters[attivo]', options.active)
        options?.typeName && url.searchParams.append('Filters[nomeTipologia]', options.typeName)
        options?.typeId && url.searchParams.append('Filters[idTipologieFotovoltaico]', options.typeId)
        options?.facilityId && url.searchParams.append('Filters[idImpianto]', options.facilityId)
        options?.facilityName && url.searchParams.append('Filters[nomeImpianto]', options.facilityName)

        const fotovoltaicoPaginationDTO = await getRequest<FotovoltaicoPaginationDTO>({
            url,
            next: { tags: [PHOTOVOLTAIC_TAG] },
            ...fetchOptions,
        })
        const plants = mapToPhotovoltaicPlants(fotovoltaicoPaginationDTO)
        return plants
    },
    getAll: async () => {
        const url = new URL(`${PHOTOVOLTAIC_URL}/Pagination`)
        url.searchParams.append('PageSize', '999999')
        const fotovoltaicoPaginationDTO = await getRequest<FotovoltaicoPaginationDTO>({
            url,
            next: { tags: [PHOTOVOLTAIC_TAG] },
            ...fetchOptions,
        })
        const plantsWithPagination = mapToPhotovoltaicPlants(fotovoltaicoPaginationDTO)
        return plantsWithPagination.plants ?? []
    },
    getAllModels: async () => {
        const url = new URL(`${PHOTOVOLTAIC_URL}/configuration`)
        const tipologieFotovoltaicoDTO = await getRequest<TipologieFotovoltaicoDTO[]>({
            url,
            ...fetchOptions,
            next: { tags: [PHOTOVOLTAIC_TAG] },
        })
        const allModels = tipologieFotovoltaicoDTO.map(mapToPhotovoltaicModel)
        return allModels
    },
    post: async (plant) => {
        const nuovoFotovoltaicoDTO = mapToFotovoltaicoDTO(plant)
        const url = new URL(PHOTOVOLTAIC_URL)
        const fotovoltaicoDTO = await postJsonRequest<NuovoFotovoltaicoDTO>({
            url,
            body: nuovoFotovoltaicoDTO,
            ...fetchOptions,
        })
        const newPhotovoltaicPlant = mapToPhotovoltaicPlant(fotovoltaicoDTO) as PhotovoltaicPlant
        return newPhotovoltaicPlant
    },
    put: async (plant) => {
        const fotovoltaicoDTO = mapToFotovoltaicoDTO(plant)
        const url = new URL(PHOTOVOLTAIC_URL)
        const responseFotovoltaicoDTO = await putJsonRequest<NuovoFotovoltaicoDTO>({
            url,
            body: fotovoltaicoDTO,
            ...fetchOptions,
        })
        const responsePhotovoltaicPlant = mapToPhotovoltaicPlant(responseFotovoltaicoDTO) as PhotovoltaicPlant
        return responsePhotovoltaicPlant
    },
    patchFacility: async (id, facilityId) => {
        const url = new URL(`${PHOTOVOLTAIC_URL}/${id.toString()}/impianto/${facilityId.toString()}`)
        const responseFotovoltaicoDTO = await patchJsonRequest<NuovoFotovoltaicoDTO>({ url, ...fetchOptions })
        const responsePhotovoltaicPlant = mapToPhotovoltaicPlant(responseFotovoltaicoDTO) as PhotovoltaicPlant
        return responsePhotovoltaicPlant
    },
    delete: async (id) => {
        const url = new URL(`${PHOTOVOLTAIC_URL}/${id.toString()}`)
        await deleteJsonRequest({ url, ...fetchOptions })
    },
    getProduction: async (id, options) => {
        const url = new URL(`${PHOTOVOLTAIC_URL}/impianto/${id.toString()}/production`)
        const { fromDate, strategy, onlyLastValue, productionFTV, photovoltaicDateStrategy, from, to, productionType } = options || {}
        fromDate && url.searchParams.append('dateFrom', fromDate)
        from && url.searchParams.append('dateFrom', from)
        to && url.searchParams.append('dateTo', to)
        strategy && url.searchParams.append('aggregationStrategy', strategy.toString())
        // Questi tre sono enum in cui lo 0 è un valore valido (POWER, AC, Daily): con `x && append(...)`
        // lo zero è falsy e non viene mai inviato, restando appeso ai default del backend.
        if (productionType != null) url.searchParams.append('productionType', productionType.toString())
        if (productionFTV != null) url.searchParams.append('fotovoltaicoProduction', productionFTV.toString())
        if (photovoltaicDateStrategy != null)
            url.searchParams.append('fotovoltaicoDataStrategy', photovoltaicDateStrategy.toString())
        onlyLastValue && url.searchParams.append('lastValue', 'true')

        const datiMisureFotovoltaicoDTO = await getRequest<DatiMisureFotovoltaicoDTO | null>({
            url,
            ...fetchOptions,
            next: { tags: [PHOTOVOLTAIC_TAG] },
        })
        if (!datiMisureFotovoltaicoDTO) return { isAggregated: false, readings: [] }
        const production = mapToPhotovoltaicReadings(datiMisureFotovoltaicoDTO)
        return production
    },
    getConsumption: async (id, options) => {
        const url = new URL(`${PHOTOVOLTAIC_URL}/impianto/${id.toString()}/consumption`)
        const { fromDate, strategy, onlyLastValue, productionFTV, photovoltaicDateStrategy, from, to } = options || {}
        fromDate && url.searchParams.append('dateFrom', fromDate)
        from && url.searchParams.append('dateFrom', from)
        to && url.searchParams.append('dateTo', to)
        strategy && url.searchParams.append('aggregationStrategy', strategy.toString())
        if (productionFTV != null) url.searchParams.append('fotovoltaicoProduction', productionFTV.toString())
        if (photovoltaicDateStrategy != null)
            url.searchParams.append('fotovoltaicoDataStrategy', photovoltaicDateStrategy.toString())
        onlyLastValue && url.searchParams.append('lastValue', 'true')

        const datiMisureFotovoltaicoDTO = await getRequest<DatiMisureFotovoltaicoDTO | null>({
            url,
            ...fetchOptions,
            next: { tags: [PHOTOVOLTAIC_TAG] },
        })

        if (!datiMisureFotovoltaicoDTO) return { isAggregated: false, readings: [] }
        const consuption = mapToPhotovoltaicReadings(datiMisureFotovoltaicoDTO)
        return consuption
    },
    getCo2: async (id, options) => {
        const url = new URL(`${PHOTOVOLTAIC_URL}/impianto/${id.toString()}/co2`)
        const { fromDate, strategy, onlyLastValue, productionFTV, photovoltaicDateStrategy, from, to } = options || {}
        fromDate && url.searchParams.append('dateFrom', fromDate)
        from && url.searchParams.append('dateFrom', from)
        to && url.searchParams.append('dateTo', to)
        strategy && url.searchParams.append('aggregationStrategy', strategy.toString())
        if (productionFTV != null) url.searchParams.append('fotovoltaicoProduction', productionFTV.toString())
        if (photovoltaicDateStrategy != null)
            url.searchParams.append('fotovoltaicoDataStrategy', photovoltaicDateStrategy.toString())
        onlyLastValue && url.searchParams.append('lastValue', 'true')

        const datiMisureFotovoltaicoDTO = await getRequest<DatiMisureFotovoltaicoDTO | null>({
            url,
            ...fetchOptions,
            next: { tags: [PHOTOVOLTAIC_TAG] },
        })

        if (!datiMisureFotovoltaicoDTO) return { isAggregated: false, readings: [] }
        const co2 = mapToPhotovoltaicReadings(datiMisureFotovoltaicoDTO)
        return co2
    },
    getEstimate: async (facilityId) => {
        const url = new URL(`${PHOTOVOLTAIC_URL}/impianto/${facilityId.toString()}/stima`)
        const stimaFotovoltaicoDTO = await getRequest<StimaFotovoltaicoDTO>({
            url,
            next: { tags: [PHOTOVOLTAIC_TAG] },
            ...fetchOptions,
        })
        return mapToPhotovoltaicEstimate(stimaFotovoltaicoDTO)
    },
    putEstimate: async (facilityId, estimate) => {
        const url = new URL(`${PHOTOVOLTAIC_URL}/impianto/${facilityId.toString()}/stima`)
        const stimaFotovoltaicoDTO = await putJsonRequest<StimaFotovoltaicoDTO>({
            url,
            body: mapToStimaFotovoltaicoDTO(estimate),
            ...fetchOptions,
        })
        return mapToPhotovoltaicEstimate(stimaFotovoltaicoDTO)
    },
})

export { photovoltaicCRUD, type PhotovoltaicCrudType, type PhotovoltaicGetOptions, PHOTOVOLTAIC_TAG }
